import { useState, useEffect } from 'react';
import { RefreshCw, X, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const UpdateBanner = () => {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [isUpToDate, setIsUpToDate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    // Check if service workers are supported
    if ('serviceWorker' in navigator) {
      // Listen for new service worker updates
      navigator.serviceWorker.ready.then((registration) => {
        // Check if there's already a waiting service worker
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowUpdateBanner(true);
          setIsUpToDate(false);
        } else {
          // No waiting worker means we're up to date
          setIsUpToDate(true);
        }

        // Listen for new service worker installations
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              // When the new service worker is installed and waiting
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setWaitingWorker(newWorker);
                setShowUpdateBanner(true);
                setIsUpToDate(false);
              }
            });
          }
        });
      });

      // Handle controller change (when a new SW takes over)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });

      // Check for updates periodically (every 5 minutes)
      const checkInterval = setInterval(() => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update().catch(console.error);
        });
      }, 5 * 60 * 1000);

      return () => clearInterval(checkInterval);
    } else {
      // No service worker support - assume up to date
      setIsUpToDate(true);
    }
  }, []);

  // Manual check for updates
  const checkForUpdates = async () => {
    setIsChecking(true);
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
        
        // Check if there's a waiting worker after update
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowUpdateBanner(true);
          setIsUpToDate(false);
        } else {
          // Force reload to get latest version
          window.location.reload();
        }
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Update check failed:', error);
      window.location.reload();
    }
    setIsChecking(false);
  };

  const handleUpdate = () => {
    if (waitingWorker) {
      // Tell the waiting service worker to skip waiting
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Fallback: just reload the page
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setShowUpdateBanner(false);
  };

  // Show clickable "Check for Updates" button when up to date
  if (isUpToDate && !showUpdateBanner) {
    return (
      <div className="fixed bottom-4 right-4 z-50 mb-[env(safe-area-inset-bottom,0px)]">
        <button
          onClick={checkForUpdates}
          disabled={isChecking}
          className="bg-teal-500 hover:bg-teal-600 text-white rounded-full shadow-lg px-4 py-2 flex items-center gap-2 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">
            {isChecking ? 'Checking...' : 'Check for Updates'}
          </span>
        </button>
      </div>
    );
  }

  // Show prominent update banner when new version available
  if (!showUpdateBanner) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom-5 duration-300">
      <div className="max-w-lg mx-auto bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl shadow-2xl p-5 border-2 border-orange-400">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-lg">🎉 New Version Available!</p>
            <p className="text-sm text-orange-100 mt-1">
              We've made improvements! Tap below to get the latest features.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <Button 
                onClick={handleUpdate}
                className="bg-white text-orange-600 hover:bg-orange-50 font-bold px-6 py-2 rounded-full shadow-lg flex items-center gap-2"
                data-testid="update-app-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Update Now
              </Button>
              <button 
                onClick={handleDismiss}
                className="text-orange-200 hover:text-white text-sm underline"
              >
                Later
              </button>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateBanner;
